import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { BookOpen, CheckCircle, Clock, Settings, Lock } from 'lucide-react';
import { toast } from 'sonner';
import BookSettingsModal from '../components/BookSettingsModal';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [booksProgress, setBooksProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsModal, setSettingsModal] = useState({ open: false, book: null });

  useEffect(() => {
    loadCourseDetails();
  }, [courseId]);

  const loadCourseDetails = async () => {
    try {
      const [courseRes, progressRes] = await Promise.all([
        api.get(`/courses`),
        api.get(`/courses/${courseId}/progress`),
      ]);

      const found = courseRes.data.find((c) => c.course_id === courseId);
      if (found) setCourse(found);
      setBooksProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to load course details:', error);
      toast.error('فشل تحميل تفاصيل المساق');
    } finally {
      setLoading(false);
    }
  };

  const openBook = (bookKey) => {
    navigate(`/courses/${courseId}/read/${bookKey}`);
  };

  const getMilestones = (totalPages) => {
    const milestones = [];
    for (let i = 50; i <= totalPages; i += 50) milestones.push(i);
    return milestones;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-accent">جارٍ التحميل...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-16">
        <p className="text-lg text-muted-foreground">المساق غير موجود</p>
      </div>
    );
  }

  const completedCount = booksProgress.filter((b) => b.is_completed).length;
  const totalPercent =
    booksProgress.length > 0
      ? Math.round(booksProgress.reduce((sum, b) => sum + b.progress_percent, 0) / booksProgress.length)
      : 0;

  return (
    <div className="space-y-8" data-testid="course-detail-page">
      {/* Course Header */}
      <div
        className="relative h-64 rounded-2xl overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(193, 154, 107, 0.9), rgba(93, 64, 55, 0.9)), url('${course.image_url || ''}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center text-center p-8">
          <div>
            <span className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
              {course.category}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{course.title}</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">{course.description}</p>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="p-8 bg-gradient-to-br from-secondary/10 to-primary/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-4xl font-bold text-primary mb-2" data-testid="books-count">{booksProgress.length}</p>
            <p className="text-muted-foreground">عدد الكتب</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-secondary mb-2" data-testid="completed-count">{completedCount}</p>
            <p className="text-muted-foreground">الكتب المكتملة</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-accent mb-2" data-testid="total-percent">{totalPercent}%</p>
            <p className="text-muted-foreground">نسبة الإنجاز</p>
          </div>
        </div>
      </Card>

      {/* Timeline of Books */}
      <div>
        <h2 className="text-3xl font-bold text-accent mb-8">الخط الزمني للكتب</h2>
        <div className="relative">
          {/* Background track line */}
          <div className="absolute right-8 top-0 bottom-0 w-1 bg-muted/40 rounded-full" />
          {/* Animated progress line */}
          <div
            className="absolute right-8 top-0 w-1 rounded-full"
            style={{
              height: `${totalPercent}%`,
              background: 'linear-gradient(180deg, #C19A6B 0%, #8B6F47 60%, #C19A6B 100%)',
              transition: 'height 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 0 8px rgba(193,154,107,0.4)',
            }}
          />
          {/* Moving dot on progress line */}
          <div
            className="absolute right-[26px] w-4 h-4 rounded-full z-20"
            style={{
              top: `${totalPercent}%`,
              background: 'radial-gradient(circle, #C19A6B 40%, #8B6F47 100%)',
              boxShadow: '0 0 12px rgba(193,154,107,0.6), 0 0 4px rgba(193,154,107,0.8)',
              transition: 'top 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'translateY(-50%)',
            }}
          >
            <div className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: 'rgba(193,154,107,0.3)' }} />
          </div>

          <div className="space-y-16">
            {booksProgress.map((book, index) => {
              const milestones = getMilestones(book.effective_total_pages);
              const currentMilestoneIdx = milestones.filter((m) => book.pages_read >= m).length;

              return (
                <div key={book.book_key} className="relative" data-testid={`book-${index}`}>
                  {/* Node dot */}
                  <div
                    className="absolute right-6 top-8 w-5 h-5 rounded-full border-4 z-10 transition-all duration-500"
                    style={{
                      borderColor: 'var(--background)',
                      backgroundColor: book.is_completed ? '#22c55e' : book.is_unlocked ? '#C19A6B' : '#d4d4d8',
                      boxShadow: book.is_completed ? '0 0 10px rgba(34,197,94,0.4)' : book.is_unlocked ? '0 0 10px rgba(193,154,107,0.4)' : 'none',
                    }}
                  />

                  <Card className={`mr-16 hover:shadow-xl transition-all duration-300 ${!book.is_unlocked ? 'opacity-60' : ''}`}>
                    <div className="grid md:grid-cols-4 gap-0">
                      <div className="h-48 md:h-auto bg-cover bg-center relative" style={{ backgroundImage: `url('${book.image_url || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?auto=format&fit=crop&q=80&w=800'}')` }}>
                        {book.is_completed && (
                          <div className="absolute inset-0 bg-green-600/60 flex items-center justify-center"><CheckCircle className="w-16 h-16 text-white" /></div>
                        )}
                        {!book.is_unlocked && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Lock className="w-12 h-12 text-white" /></div>
                        )}
                      </div>
                      <div className="md:col-span-3 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">كتاب {index + 1}</span>
                              <span className="text-sm text-muted-foreground">{book.effective_total_pages} صفحة</span>
                              {book.is_completed && <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">مكتمل</span>}
                              {!book.is_unlocked && <span className="bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm font-medium">مقفل</span>}
                            </div>
                            <h3 className="text-2xl font-bold text-accent mb-2">{book.title}</h3>
                            <p className="text-muted-foreground mb-4">المؤلف: {book.author}</p>

                            {/* Milestones */}
                            {book.is_unlocked && milestones.length > 0 && (
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium">المستويات</span>
                                  <span className="text-sm text-muted-foreground">المستوى {currentMilestoneIdx + 1} من {milestones.length + 1}</span>
                                </div>
                                <div className="flex gap-1">
                                  {[0, ...milestones].map((milestone, idx) => {
                                    const nextMilestone = milestones[idx] || book.effective_total_pages;
                                    const isFilled = book.pages_read >= nextMilestone;
                                    const isPartial = !isFilled && book.pages_read > milestone;
                                    return (
                                      <div key={idx} className={`flex-1 h-3 rounded-full transition-all duration-700 ${isFilled ? 'bg-primary' : isPartial ? 'bg-primary/40' : 'bg-muted'}`}
                                        title={`المستوى ${idx + 1}: ${milestone}-${nextMilestone} صفحة`} />
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Progress Bar */}
                            {book.is_unlocked && (
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium">التقدم الإجمالي</span>
                                  <span className="text-sm text-muted-foreground">{book.pages_read} / {book.effective_total_pages}</span>
                                </div>
                                <Progress value={book.progress_percent} className="h-2" />
                              </div>
                            )}
                          </div>

                          {book.is_completed ? (
                            <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
                          ) : book.is_unlocked ? (
                            <Clock className="w-6 h-6 text-primary shrink-0" />
                          ) : (
                            <Lock className="w-6 h-6 text-muted shrink-0" />
                          )}
                        </div>

                        <div className="flex gap-3">
                          <Button onClick={() => openBook(book.book_key)} className="bg-primary hover:bg-primary/90" disabled={!book.pdf_url || !book.is_unlocked} data-testid={`read-book-${index}`}>
                            <BookOpen className="w-5 h-5 ml-2" />{book.is_unlocked ? 'قراءة الكتاب' : 'مقفل'}
                          </Button>
                          <Button variant="outline" onClick={() => setSettingsModal({ open: true, book })} disabled={!book.is_unlocked} data-testid={`settings-book-${index}`}>
                            <Settings className="w-5 h-5 ml-2" />الإعدادات
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}

            {/* End marker */}
            <div className="relative flex items-center gap-4">
              <div className="absolute right-6 w-5 h-5 rounded-full z-10" style={{
                background: totalPercent >= 100 ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#d4d4d8',
                boxShadow: totalPercent >= 100 ? '0 0 12px rgba(34,197,94,0.5)' : 'none',
              }} />
              <div className="mr-16 text-sm font-semibold" style={{ color: totalPercent >= 100 ? '#16a34a' : '#a3a3a3' }}>
                {totalPercent >= 100 ? 'تم إتمام المساق بنجاح!' : 'نهاية المساق'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BookSettingsModal
        open={settingsModal.open}
        onClose={() => setSettingsModal({ open: false, book: null })}
        book={settingsModal.book}
        onSave={() => loadCourseDetails()}
      />
    </div>
  );
};

export default CourseDetailPage;
